import { uniq } from 'lodash';
import { ReactElement, useEffect, useLayoutEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import { pruneAnalyticsDatabases } from '@treams/treams-analytics';
import MainRouter, { MainRoutes } from '@treams/treams-app';
import {
	useAuthenticatedUser,
	useMixpanel,
	useSessionSlots,
	useViewerRoles,
	useWorkspaceAlias,
	useWorkspaceLocale,
} from '@treams/treams-hooks';
import { AnalyticsIntervalProvider } from '@treams/treams-hooks/src/useAnalyticsInterval';
import * as graphql from '@treams/treams-network';
import { Portal } from '@treams/treams-ui';

import { StreamingTranscriptionProvider } from '../../../../../../src/Recording/StreamingTranscriptionContext';
import { TranscriptionRecordingProvider } from '../../../../../../src/Recording/TranscriptionRecordingContext';
import ModalNavigation from '../../../../../../src/navigation/ModalNavigation';
import HelpdeskContainer from '../../../../../../src/packages/kakapo-ui-application/common/containers/HelpdeskContainer';
import AuthorizationContext from '../../../../../../src/packages/kakapo-ui-application/contexts/AuthorizationContext';
import FullPageLoadingContainer from '../../../../../../src/packages/kakapo-ui-application/pages/loading/FullPageLoadingContainer';
import publicRoutes from '../../../../../../src/packages/kakapo-ui-application/routers/publicRoutes';
import PopoverProvider from '../../../../../../src/packages/kakapo-ui-kit/components/popovers/PopoverProvider';
import Logout from './Logout';
import PrivacyConsent from './PrivacyConsent';

function useMixpanelIdentify(id?: string, props?: Record<string, string>) {
	const mixpanel = useMixpanel();

	useLayoutEffect(() => {
		if (id) {
			mixpanel?.identify(id);

			for (const key in props ?? {}) {
				mixpanel?.people.set(key, props?.[key]);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, !!mixpanel, JSON.stringify(props ?? {})]);
}

function useMixpanelPeople(people?: Record<string, string>) {
	const mixpanel = useMixpanel();

	useLayoutEffect(() => {
		if (people) {
			mixpanel?.people.set(people);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(people), !!mixpanel]);
}

/**
 * Analytics snapshots are permission-scoped per user and cached in IndexedDB.
 * Drop the ones that no longer belong to a signed-in account, so a device only
 * retains data for the accounts currently in the session.
 *
 * Lives here rather than in AnalyticsProvider: the provider only mounts on the
 * analytics and agent routes, so users without analytics access would never
 * trigger the cleanup.
 */
function useAnalyticsDatabasePrune() {
	const { slots, loading } = useSessionSlots();
	const workspaceAlias = useWorkspaceAlias();

	// Serialized so the effect keys on the accounts themselves, not on the
	// array identity the query hook recreates.
	const userIDs = slots
		.map((slot) => slot.userID)
		.sort()
		.join(',');

	useEffect(() => {
		// Pruning against a half-loaded slot list would delete live databases.
		if (loading || !userIDs) return;

		pruneAnalyticsDatabases(workspaceAlias, userIDs.split(',')).catch(() => {
			// Best effort: logout clears everything, and the next boot retries.
		});
	}, [workspaceAlias, userIDs, loading]);
}

function MixpanelIdentity() {
	const authenticatedUser = useAuthenticatedUser();

	const roles = useViewerRoles();
	useMixpanelIdentify(authenticatedUser?.id);

	useMixpanelPeople(
		authenticatedUser && {
			$name: `${authenticatedUser.firstName} ${authenticatedUser.lastName}`,
			$email: authenticatedUser.email,
			$roles: uniq(roles.roles.map((role) => role.role.type)).join(', '),
		},
	);

	return null;
}

export default function AppAuthenticated(): ReactElement {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const viewer = graphql.useViewerQuery();

	const authenticatedUser = viewer.data?.viewer;
	useWorkspaceLocale();
	useAnalyticsDatabasePrune();

	// Sign out only on real auth errors, not transient failures.
	// sessionSlotMismatch excluded: useHttpLink reloads instead.
	const isDeadSession = viewer.error?.graphQLErrors?.some(({ extensions }) => {
		const key = (extensions as { error?: { key?: string } })?.error?.key;

		return (
			key?.startsWith('authorization.errors') && key !== 'authorization.errors.sessionSlotMismatch'
		);
	});

	// Above the viewer gate: broken sessions must still reach Logout.
	if (location.pathname === '/logout' || isDeadSession) {
		return <Logout />;
	}

	if (!authenticatedUser) {
		return <FullPageLoadingContainer />;
	}

	return (
		<AuthorizationContext.Provider
			value={{
				authenticatedUser: authenticatedUser as graphql.User,
			}}
		>
			<MixpanelIdentity />
			<Portal.Provider>
				<PopoverProvider>
					{authenticatedUser?.privacyConsent?.version !== '1.5' ? (
						<HelpdeskContainer>
							<PrivacyConsent />
						</HelpdeskContainer>
					) : (
						<AnalyticsIntervalProvider>
							<TranscriptionRecordingProvider>
								<StreamingTranscriptionProvider>
									<Switch>
										{[...publicRoutes]}
										<Route>
											<MainRouter />
										</Route>
									</Switch>
									<ModalNavigation url={params.get('modal') ?? ''}>
										<MainRoutes />
									</ModalNavigation>
								</StreamingTranscriptionProvider>
							</TranscriptionRecordingProvider>
						</AnalyticsIntervalProvider>
					)}
				</PopoverProvider>
			</Portal.Provider>
		</AuthorizationContext.Provider>
	);
}
